import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

function errText(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ") || "Something went wrong.";
  return e?.message || "Something went wrong.";
}

function LoginForm({ onPassed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      onPassed(data);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="vault-card" onSubmit={submit}>
      <h2>Restricted access</h2>
      <p className="vault-hint">Sign in to continue.</p>
      <label className="field">
        <span>Email</span>
        <input type="email" autoComplete="username" required value={email}
          onChange={(e) => setEmail(e.target.value)} data-testid="vault-email-input" />
      </label>
      <label className="field">
        <span>Password</span>
        <input type="password" autoComplete="current-password" required value={password}
          onChange={(e) => setPassword(e.target.value)} data-testid="vault-password-input" />
      </label>
      {error && <p className="vault-error" role="alert" data-testid="vault-login-error">{error}</p>}
      <button className="btn cta vault-btn" disabled={busy} data-testid="vault-login-button">
        {busy ? "Checking…" : "Continue"}
      </button>
    </form>
  );
}

function TwoFactor({ mfaToken, enrolled, onAuthed }) {
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (enrolled) return;
    http.post("/auth/2fa/setup", { mfa_token: mfaToken })
      .then(({ data }) => { setQr(data.qr); setSecret(data.secret); })
      .catch((e) => setError(errText(e)));
  }, [enrolled, mfaToken]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await http.post("/auth/2fa/verify", { mfa_token: mfaToken, code });
      onAuthed();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="vault-card" onSubmit={submit}>
      <h2>Two-factor authentication</h2>
      {!enrolled && (
        <>
          <p className="vault-hint">
            Scan this QR code with Google Authenticator, 1Password or any TOTP app, then enter the 6-digit code.
          </p>
          {qr && <img src={qr} alt="2FA QR code" className="vault-qr" data-testid="vault-qr" />}
          {secret && <p className="vault-secret">Manual key: <code>{secret}</code></p>}
        </>
      )}
      {enrolled && <p className="vault-hint">Enter the 6-digit code from your authenticator app.</p>}
      <label className="field">
        <span>6-digit code</span>
        <input inputMode="numeric" pattern="[0-9]*" maxLength={6} required value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          data-testid="vault-otp-input" />
      </label>
      {error && <p className="vault-error" role="alert" data-testid="vault-otp-error">{error}</p>}
      <button className="btn cta vault-btn" disabled={busy || code.length !== 6} data-testid="vault-otp-button">
        {busy ? "Verifying…" : "Verify"}
      </button>
    </form>
  );
}

function Dashboard({ onLogout }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/waitlist?limit=500");
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        try {
          await http.post("/auth/refresh");
          const { data } = await http.get("/admin/waitlist?limit=500");
          setData(data);
          return;
        } catch {
          onLogout();
          return;
        }
      }
      setError(errText(e));
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const res = await http.get("/admin/waitlist/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "waitlist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = async () => {
    try { await http.post("/auth/logout"); } catch {}
    onLogout();
  };

  return (
    <div className="vault-dash" data-testid="vault-dashboard">
      <div className="vault-dash-head">
        <div>
          <h2>Waitlist</h2>
          <p className="vault-hint" data-testid="vault-count">
            {data ? `${data.total} signup${data.total === 1 ? "" : "s"}` : "Loading…"}
          </p>
        </div>
        <div className="vault-actions">
          <button className="btn vault-ghost" onClick={exportCsv} data-testid="vault-export-button">Export CSV</button>
          <button className="btn vault-ghost" onClick={logout} data-testid="vault-logout-button">Log out</button>
        </div>
      </div>
      {error && <p className="vault-error">{error}</p>}
      {data && (
        <div className="vault-table-wrap">
          <table className="vault-table" data-testid="vault-table">
            <thead>
              <tr><th>Email</th><th>Phone</th><th>Email ✓</th><th>SMS ✓</th><th>Klaviyo</th><th>Joined</th></tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.email}>
                  <td>{r.email}</td>
                  <td>{r.phone || "—"}</td>
                  <td>{r.email_consent ? "Yes" : "No"}</td>
                  <td>{r.sms_consent ? "Yes" : "No"}</td>
                  <td>{r.klaviyo_synced ? "Synced" : "—"}</td>
                  <td>{r.joined_at ? new Date(r.joined_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan={6} className="vault-empty">No signups yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Vault() {
  const [stage, setStage] = useState("checking");
  const [mfa, setMfa] = useState(null);

  useEffect(() => {
    http.get("/auth/me")
      .then(() => setStage("dash"))
      .catch(() => setStage("login"));
  }, []);

  return (
    <div className="page vault-page">
      <header className="brand"><span className="wordmark">Frank369.AI</span></header>
      <main className="vault-main">
        {stage === "checking" && <p className="vault-hint">Loading…</p>}
        {stage === "login" && (
          <LoginForm onPassed={(d) => { setMfa(d); setStage("otp"); }} />
        )}
        {stage === "otp" && mfa && (
          <TwoFactor mfaToken={mfa.mfa_token} enrolled={mfa.totp_enrolled}
            onAuthed={() => setStage("dash")} />
        )}
        {stage === "dash" && <Dashboard onLogout={() => { setMfa(null); setStage("login"); }} />}
      </main>
    </div>
  );
}
